import {
  APIFieldType,
  AssetType,
  APIComponentAttributes,
  APIEntityType,
  APIReferenceAttributes,
  Named,
  Described,
  LocalFilterId,
  TripleDirection,
  TripleQualifier,
} from '../apiTypes';
import { ArdoqId, Email, Entity, MetaData, Versioned } from '../coreTypes';
import { QueryBuilderSubquery } from '../advancedSearchApiTypes';
import { APIResourcePermissionAttributes } from '../resourcePermissionsAPITypes';
import {
  PersistedSurveyQuestion,
  SurveyQuestionType,
} from './surveyQuestionApiTypes';
import { ViewpointBuilderFilters } from '../traversal';

export type HeaderField = {
  name: string;
  label: string;
};

export type SurveyError = {
  errors: {
    data: any;
    errorType: string;
    formatArgs: any;
    message: string;
    status: number;
  };
};

export enum SurveyResultFilteringType {
  REFERENCE = 'reference',
  HIERARCHY = 'hierarchy',
}

export type ReferenceFilterProperties = {
  workspaceId?: ArdoqId;
  componentTypeName?: string | null;
  outgoing?: boolean;
  referenceTypeName?: string;
};

export type SurveyFilterSetupAttributes = {
  properties: ReferenceFilterProperties;
  type: SurveyResultFilteringType | undefined;
  enabled: boolean | undefined;
  label: string | undefined;
  sortByField?: string | null;
};

export type SurveySectionStatus = 'initial' | 'entered' | 'left';

export type SurveyMiscAttributes = {
  overviewButtonText: string;
  newCompButtonText: string;
  disableLandingPage: boolean;
  generalInformationSectionStatus?: SurveySectionStatus;
  defineTheDataSectionStatus?: SurveySectionStatus;
  questionsSectionStatus?: SurveySectionStatus;
  feedbackMessageSectionStatus?: SurveySectionStatus;
  landingPageSectionStatus?: SurveySectionStatus;
  resultsDisplaySectionStatus?: SurveySectionStatus;
  ardoqDiscoverSectionStatus?: SurveySectionStatus;
  changeApprovalSectionStatus?: SurveySectionStatus;
  permissionsAndVisibilitySectionStatus?: SurveySectionStatus;
};

export type APISurveyPendingApprovals = {
  _id: ArdoqId;
  name: string;
  total: number;
};

export type APISurveyPendingApprovalsSummary = Partial<
  Record<ArdoqId, APISurveyPendingApprovals>
>;

export type ChangeRequestAction = 'update' | 'create' | 'delete';

// Bare change-request fields as returned inline by the component-survey
// approval-status endpoint: identity + the change being requested. The
// versioned/wrapped CRUD entity (APISurveyChangeRequest) composes from this.
export type SurveyChangeRequest = Entity &
  MetaData & {
    action: ChangeRequestAction;
    branch: ArdoqId;
    component: ArdoqId;
  };

export type APISurveyChangeRequest = SurveyChangeRequest &
  Versioned & {
    ardoq: { entityType: APIEntityType.CHANGE_REQUEST };
  };

export type DeletedOnMainlineData = {
  components: ArdoqId[];
  references: ArdoqId[];
};

export type APISurveyApprovedComponentChanges = {
  update?: APIComponentAttributes[];
  create?: APIComponentAttributes[];
  delete?: ArdoqId[];
};

export type APISurveyApprovedChanges = APISurveyApprovedComponentChanges & {
  components?: APISurveyApprovedComponentChanges;
  references?: {
    update?: APIReferenceAttributes[];
    create?: APIReferenceAttributes[];
    delete?: ArdoqId[];
  };
};

export type SurveyApprovalFallbackAudience = {
  audienceType: 'user';
  audiencesIds: ArdoqId[];
};

export type SurveyApprovalUsersAndGroupsAudience = {
  audienceType: 'usersAndGroups';
  groupIds: ArdoqId[];
  userIds: ArdoqId[];
};

export type SurveyApprovalRelationship = {
  outgoing: boolean;
  referenceTypeName: string;
  componentTypeName: string;
  emailField: string | null;
};

export type SurveyApprovalRelationshipsAudience = {
  audienceType: 'relationships';
  relationships: SurveyApprovalRelationship[];
};

/**
 * Survey approval traversals are persisted with the type names the user
 * picked at the time. When a referenced component type is later deleted from
 * the metamodel, the backend returns `sourceType` / `targetType` as `null` so
 * the FE can detect the dangling reference and surface a validation error.
 */
export type SurveyApprovalDirectedTriple = {
  sourceType: string | null;
  targetType: string | null;
  referenceType: string;
  direction: TripleDirection;
  qualifier?: TripleQualifier;
  sourceFilter?: LocalFilterId;
  targetFilter?: LocalFilterId;
  referenceFilter?: LocalFilterId;
  isVirtual?: true;
  referenceTypeId?: ArdoqId;
};

export type SurveyApprovalTraversal = {
  paths: SurveyApprovalDirectedTriple[][];
  filters?: ViewpointBuilderFilters;
};

export type SurveyApprovalTraversalAudience = {
  audienceType: 'traversal';
  traversal: SurveyApprovalTraversal;
  userResolveFields: { componentTypeName: string; fieldName: string }[];
};

export type SurveyApprovalAudience =
  | SurveyApprovalUsersAndGroupsAudience
  | SurveyApprovalRelationshipsAudience
  | SurveyApprovalTraversalAudience;

export type SurveyApprovalAudienceType = SurveyApprovalAudience['audienceType'];

export type SurveyApprovalAudiences =
  | SurveyApprovalUsersAndGroupsAudience[]
  | SurveyApprovalRelationshipsAudience[]
  | SurveyApprovalTraversalAudience[];

export type SurveyApprovalFlow = {
  enabled?: boolean;
  fallbackAudience: SurveyApprovalFallbackAudience;
  selectors?: SurveyApprovalAudiences;
  sendEmails?: boolean;
  shortCircuit?: boolean;
};

// Approval data for a (component, survey) pair: form validity + any pending
// change request. Composed into the wire shape and the enriched view shape.
export type ComponentSurveyApprovalFields = {
  valid: boolean;
  changeRequest: SurveyChangeRequest | null;
};

// Wire shape from /api/survey/component/{componentId}: a survey identifier
// stub plus its approval state for the requested component.
export type ComponentSurveyApprovalStatus = ComponentSurveyApprovalFields & {
  survey: Entity & Named & Described;
};

export type APISurveyAttributes = Entity &
  MetaData &
  Named &
  Described &
  Versioned & {
    allowComponentCreation: boolean;
    allowComponentDeletion: boolean;
    approvalFlow?: SurveyApprovalFlow;
    ardoq: { 'entity-type': AssetType.SURVEY };
    ardoqPersistent: {
      useCase?: string;
      bundleCopy?: {
        at: string;
        sourceBundle: { _id: ArdoqId; name: string };
        sourceOrgLabel: string;
      };
    } | null;
    componentSearch: QueryBuilderSubquery;
    componentTypeIds: null;
    componentTypeName: string;
    contactEmail?: Email;
    customHeaderFields: HeaderField[];
    description: string;
    discoverEnabled?: boolean;
    discoverLabel?: string;
    discoverPriority?: boolean;
    errors?: SurveyError[];
    filterSetup: SurveyFilterSetupAttributes | null;
    misc: SurveyMiscAttributes;
    name?: string;
    newComponentParentId?: ArdoqId | null;
    published: boolean;
    questions: PersistedSurveyQuestion[];
    responseFeedbackMessage: string | null;
    responseFeedbackOnSurveySubmit: boolean | null;
    surveyNotificationsAudience?: ArdoqId[] | null;
    surveyNotificationsEnabled: boolean | null;
    surveyNotificationsTimezone?: string | null;
    workspace: ArdoqId | null;
  };

export type ComponentSurveyWithApprovalStatus = APISurveyAttributes &
  ComponentSurveyApprovalFields;

export type APICreateSurveyWithPermissionsResponse = {
  survey: APISurveyAttributes;
  permission: APIResourcePermissionAttributes;
};

export type APIResponseLogDataResponse = {
  results: APISurveyResponseLog[];
  total: number;
  surveyId: ArdoqId;
};

export type APISurveyResponseLog =
  | UpdatedResponse
  | DeletedResponse
  | CreatedResponse;

export type UpdatedResponse = {
  currentVal: any;
  lastModifiedByName: string;
  lastUpdated: string;
  name: string;
  previousVal: any;
  component: { name: string; url: string };
  type: APIFieldType;
};

export type CreatedResponse = {
  lastModifiedByName: string;
  created: string;
  name: string;
  url: string;
};

export type DeletedResponse = {
  lastModifiedByName: string;
  deleted: string;
  name: string;
};

export type APISurveyReferenceComponent = {
  _id?: string;
  name: string;
  namePath?: string[];
  identifierValue?: any;
};

export type APISurveyReferenceAnswerValue = {
  component: APISurveyReferenceComponent;
  referenceId?: string;
  outgoing?: boolean;
  answers: APISurveyComponentAnswer[];
};

export type APISurveyReferenceAnswer = {
  questionId: ArdoqId;
  type: SurveyQuestionType.REFERENCE;
  value: APISurveyReferenceAnswerValue[];
  totalReferences: number;
};

export type APISurveyComponentAnswer = {
  questionId: ArdoqId;
  type: SurveyQuestionType;
  value: any;
};

export type APISurveyParentAnswer = {
  questionId: ArdoqId;
  type: SurveyQuestionType.ATTRIBUTE;
  value: ArdoqId | null;
  parentName?: string;
};

export type APISurveyDependentQuestionAnswer = APISurveyComponentAnswer & {
  answers: APISurveyAnswer[];
};

export type APISurveyAnswer =
  | APISurveyDependentQuestionAnswer
  | APISurveyComponentAnswer
  | APISurveyReferenceAnswer
  | APISurveyParentAnswer;

export type APISurveyAnswersResponse = {
  answers: APISurveyAnswer[];
  component: APIComponentAttributes;
  changeRequest?: APISurveyChangeRequest;
  shortCircuit?: boolean;
};

export type APIComponentHierarchy = {
  defaultSort: null | string;
  componentHierarchy: Record<ArdoqId, APIComponentAttributes>;
};

export type APIFetchSurveyComponentsResponse = {
  components: APIComponentAttributes[];
  pinnedComponent: APIComponentAttributes | null;
  total: number;
  changeRequests: APISurveyChangeRequest[];
};

export enum UpdateType {
  CREATED = 'created',
  UPDATED = 'updated',
  DELETED = 'deleted',
}

export type APINewComponentSurveyFormSubmission = {
  component: {
    name: ArdoqId;
    parent: ArdoqId | undefined | null;
  };
  answers: APISurveyAnswer[];
};

export type APISurveyDeleteComponentResponse = {
  shortCircuit?: boolean;
};

export type SurveyDeleteAnswerMessage = {
  type: 'deleteAnswer';
  userIsApprover: boolean;
};

export type SurveySaveNewAnswerMessage = {
  type: 'saveNewAnswer';
  value: APISurveyAnswersResponse;
};

export type SurveySaveAnswerMessage = {
  type: 'saveAnswer';
  userIsApprover: boolean;
};

export type SurveyMessageEventData =
  | SurveyDeleteAnswerMessage
  | SurveySaveNewAnswerMessage
  | SurveySaveAnswerMessage;
